import React, { useEffect, useState } from 'react';

import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { IDatepickerRangeProps } from './DatepickerRange.types';

import Datepicker from '../Datepicker';
import GridContainer from '../GridContainer';
import Meta from '../Meta';

import './DatepickerRange.scss';

const ERROR_MSG = `The value at the start rental date can't be greater than the end rental date`;

const DatepickerRange: React.FC<IDatepickerRangeProps> = ({
	fromLabel = 'From',
	value,
	groupLabel,
	toLabel = 'To',
	handleChangeFromDate,
	handleChangeToDate,
	requiredFromDate,
	requiredToDate,
	errorFromDate,
	errorToDate,
	errorMessage,
	disableInput,
	disabled,
	isMobileDevice,
	excludeDateIntervalsFrom,
	excludeDateIntervalsTo,
	includeDateIntervalsFrom,
	includeDateIntervalsTo,
	includeDatesFrom,
	includeDatesTo,
	excludeDatesFrom,
	excludeDatesTo,
	isTodayButtonFrom,
	isTodayButtonTo,
	isClearableFrom,
	isClearableTo,
	maxDateFrom,
	maxDateTo,
	minDateFrom,
	minDateTo,
	handleCalendarOpen,
	handleCalendarClose,
	setRangeError,
	renderDayContentsFrom,
	renderDayContentsTo,
	isSingleErrorMessage = false,
}) => {
	const { from, to } = value;

	const fromValue = from ? new Date(from) : null;
	const toValue = to ? new Date(to) : null;

	const [invalidRange, setInvalidRange] = useState('');

	useEffect(() => {
		if (from && to) {
			const preparedFrom = startOfDayInUTC(new Date(from));
			const preparedTo = startOfDayInUTC(new Date(to));

			setInvalidRange(preparedFrom > preparedTo || preparedTo < preparedFrom ? errorMessage || ERROR_MSG : '');

			if (!setRangeError) return;

			if (preparedTo < preparedFrom) setRangeError(true);
			else setRangeError(false);
		}
	}, [errorMessage, from, setRangeError, to, value]);

	const onCalendarOpen = () => {
		if (!handleCalendarOpen) return;

		handleCalendarOpen();
	};

	const onCalendarClose = () => {
		if (!handleCalendarClose) return;

		handleCalendarClose();
	};

	return (
		<div>
			{groupLabel && <div className='f5gDatepickerRangeTitle'>{groupLabel}</div>}
			<GridContainer
				columns={2}
				gap='20px'
			>
				<Datepicker
					disableInput={disableInput}
					disabled={disabled}
					errorMessage={invalidRange || errorFromDate}
					excludeDateIntervals={excludeDateIntervalsFrom}
					excludeDates={excludeDatesFrom}
					handleChange={handleChangeFromDate as never}
					includeDateIntervals={includeDateIntervalsFrom}
					includeDates={includeDatesFrom}
					isClearable={isClearableFrom}
					isFullWidth
					isHiddenErrorMessage={isSingleErrorMessage}
					isMobileDevice={isMobileDevice}
					isTodayButton={isTodayButtonFrom}
					label={fromLabel}
					maxDate={maxDateFrom}
					minDate={minDateFrom}
					onCalendarClose={onCalendarClose}
					onCalendarOpen={onCalendarOpen}
					renderDayContents={renderDayContentsFrom}
					required={requiredFromDate}
					selected={fromValue}
				/>
				<Datepicker
					disableInput={disableInput}
					disabled={disabled}
					errorMessage={invalidRange || errorToDate}
					excludeDateIntervals={excludeDateIntervalsTo}
					excludeDates={excludeDatesTo}
					handleChange={handleChangeToDate as never}
					includeDateIntervals={includeDateIntervalsTo}
					includeDates={includeDatesTo}
					isClearable={isClearableTo}
					isFullWidth
					isHiddenErrorMessage={isSingleErrorMessage}
					isMobileDevice={isMobileDevice}
					isTodayButton={isTodayButtonTo}
					label={toLabel}
					maxDate={maxDateTo}
					minDate={minDateTo}
					onCalendarClose={onCalendarClose}
					onCalendarOpen={onCalendarOpen}
					renderDayContents={renderDayContentsTo}
					required={requiredToDate}
					selected={toValue}
				/>
			</GridContainer>
			{invalidRange && isSingleErrorMessage && (
				<Meta
					className='f5gDatepickerMeta'
					message={invalidRange}
				/>
			)}
		</div>
	);
};

export default DatepickerRange;
